import { Alert, Anchor, List, Stack, Text } from '@mantine/core';
import { createFileRoute,Link } from '@tanstack/react-router';

import { MarketingHero } from '../components/marketing/MarketingHero';
import { MarketingNextSteps } from '../components/marketing/MarketingNextSteps';
import { MarketingSection } from '../components/marketing/MarketingSection';
import { PublicShell } from '../components/marketing/PublicShell';
import { useCmsImage, useCmsSlot } from '../lib/cms';

export const Route = createFileRoute('/terms')({
  component: TermsPage,
});

function TermsPage() {
  // Every substantive clause body + section heading + the draft banner + hero
  // copy + closing tiles is CMS-editable (added 2026-07-04). Inline links and
  // the bold legal-entity name stay hardcoded in JSX; the prose around them is
  // editable. The Exchange Covenant list (Section 2) and hero photo remain the
  // pre-existing base-registry slots.
  const lastUpdated = useCmsSlot<'markdown'>('terms.last_updated');
  const heroTitle = useCmsSlot<'markdown'>('terms.hero_title');
  const heroLede = useCmsSlot<'markdown'>('terms.hero_lede');
  const reviewBannerTitle = useCmsSlot<'markdown'>('terms.review_banner_title');
  const reviewBannerBody = useCmsSlot<'markdown'>('terms.review_banner_body');
  const aboutEyebrow = useCmsSlot<'markdown'>('terms.about_eyebrow');
  const aboutTitle = useCmsSlot<'markdown'>('terms.about_title');
  const aboutBody = useCmsSlot<'markdown'>('terms.about_body');
  const exchangePromiseEyebrow = useCmsSlot<'markdown'>('terms.exchange_promise_eyebrow');
  const exchangePromiseTitle = useCmsSlot<'markdown'>('terms.exchange_promise_title');
  const exchangePromiseIntro = useCmsSlot<'markdown'>('terms.exchange_promise_intro');
  const exchangePromiseOutro = useCmsSlot<'markdown'>('terms.exchange_promise_outro');
  const exchangePromiseLinkLabel = useCmsSlot<'markdown'>('terms.exchange_promise_link_label');
  const exchangePromiseOutroAfter = useCmsSlot<'markdown'>('terms.exchange_promise_outro_after');
  const covenantItems = useCmsSlot<'bullet_list'>('terms.covenant_items');
  const eligibilityEyebrow = useCmsSlot<'markdown'>('terms.eligibility_eyebrow');
  const eligibilityTitle = useCmsSlot<'markdown'>('terms.eligibility_title');
  const eligibilityBody = useCmsSlot<'markdown'>('terms.eligibility_body');
  const subscriptionsEyebrow = useCmsSlot<'markdown'>('terms.subscriptions_eyebrow');
  const subscriptionsTitle = useCmsSlot<'markdown'>('terms.subscriptions_title');
  const subscriptionsBody = useCmsSlot<'markdown'>('terms.subscriptions_body');
  const conductEyebrow = useCmsSlot<'markdown'>('terms.conduct_eyebrow');
  const conductTitle = useCmsSlot<'markdown'>('terms.conduct_title');
  const conductBody = useCmsSlot<'markdown'>('terms.conduct_body');
  const disputesEyebrow = useCmsSlot<'markdown'>('terms.disputes_eyebrow');
  const disputesTitle = useCmsSlot<'markdown'>('terms.disputes_title');
  const disputesBody = useCmsSlot<'markdown'>('terms.disputes_body');
  const disputesLinkLabel = useCmsSlot<'markdown'>('terms.disputes_link_label');
  const disputesBodyAfter = useCmsSlot<'markdown'>('terms.disputes_body_after');
  const terminationEyebrow = useCmsSlot<'markdown'>('terms.termination_eyebrow');
  const terminationTitle = useCmsSlot<'markdown'>('terms.termination_title');
  const terminationBody = useCmsSlot<'markdown'>('terms.termination_body');
  const liabilityEyebrow = useCmsSlot<'markdown'>('terms.liability_eyebrow');
  const liabilityTitle = useCmsSlot<'markdown'>('terms.liability_title');
  const liabilityBody = useCmsSlot<'markdown'>('terms.liability_body');
  const governingLawEyebrow = useCmsSlot<'markdown'>('terms.governing_law_eyebrow');
  const governingLawTitle = useCmsSlot<'markdown'>('terms.governing_law_title');
  const governingLawBody = useCmsSlot<'markdown'>('terms.governing_law_body');
  const legalEntityLine = useCmsSlot<'markdown'>('terms.legal_entity_line');
  const nextTitle = useCmsSlot<'markdown'>('terms.next_title');
  const { image: heroImg, ready: heroImgReady } = useCmsImage('terms.hero_image');

  return (
    <PublicShell>
      <MarketingHero
        eyebrow={`Last updated · ${lastUpdated}`}
        title={heroTitle}
        lede={heroLede}
        {...(heroImgReady ? { backgroundImage: heroImg.url } : {})}
        backgroundImageAlt={heroImg.alt}
        backgroundImageOpacity={heroImg.opacity ?? 0.12}
      />

      <MarketingSection py="md">
        <Alert color="yellow" variant="light" title={reviewBannerTitle}>
          {reviewBannerBody}
        </Alert>
      </MarketingSection>

      <MarketingSection eyebrow={aboutEyebrow} title={aboutTitle}>
        <Text size="lg" style={{ lineHeight: 1.7 }}>
          {aboutBody}{' '}
          <strong>WWAAVE PTY LTD</strong> (ACN 689 639 423).
        </Text>
      </MarketingSection>

      <MarketingSection eyebrow={exchangePromiseEyebrow} title={exchangePromiseTitle} tinted>
        <Stack gap="md">
          <Text size="lg" style={{ lineHeight: 1.7 }}>
            {exchangePromiseIntro}
          </Text>
          <List spacing="md" size="md" style={{ lineHeight: 1.65 }}>
            {covenantItems.map((item) => (
              <List.Item key={item}>{item}</List.Item>
            ))}
          </List>
          {/* Split around the link so the prose AND the link label are editable
              (Richie couldn't reach either, 2026-08-02). Blanking all three
              hides the line rather than leaving a stray link. */}
          {exchangePromiseOutro !== '' ||
          exchangePromiseLinkLabel !== '' ||
          exchangePromiseOutroAfter !== '' ? (
            <Text size="lg" style={{ lineHeight: 1.7 }}>
              {exchangePromiseOutro}{' '}
              <Anchor component={Link} to="/our-ethos" c="brand.7" fw={500}>
                {exchangePromiseLinkLabel}
              </Anchor>
              {exchangePromiseOutroAfter}
            </Text>
          ) : null}
        </Stack>
      </MarketingSection>

      <MarketingSection eyebrow={eligibilityEyebrow} title={eligibilityTitle}>
        <Text size="lg" style={{ lineHeight: 1.7 }}>
          {eligibilityBody}
        </Text>
      </MarketingSection>

      <MarketingSection eyebrow={subscriptionsEyebrow} title={subscriptionsTitle} tinted>
        <Text size="lg" style={{ lineHeight: 1.7 }}>
          {subscriptionsBody}
        </Text>
      </MarketingSection>

      <MarketingSection eyebrow={conductEyebrow} title={conductTitle}>
        <Text size="lg" style={{ lineHeight: 1.7 }}>
          {conductBody}
        </Text>
      </MarketingSection>

      <MarketingSection eyebrow={disputesEyebrow} title={disputesTitle} tinted>
        <Text size="lg" style={{ lineHeight: 1.7 }}>
          {disputesBody}{' '}
          <Anchor component={Link} to="/dispute-resolution" c="brand.7" fw={500}>
            {disputesLinkLabel}
          </Anchor>
          {disputesBodyAfter}
        </Text>
      </MarketingSection>

      <MarketingSection eyebrow={terminationEyebrow} title={terminationTitle}>
        <Text size="lg" style={{ lineHeight: 1.7 }}>
          {terminationBody}
        </Text>
      </MarketingSection>

      <MarketingSection eyebrow={liabilityEyebrow} title={liabilityTitle} tinted>
        <Text size="lg" style={{ lineHeight: 1.7 }}>
          {liabilityBody}
        </Text>
      </MarketingSection>

      <MarketingSection eyebrow={governingLawEyebrow} title={governingLawTitle}>
        <Stack gap="md">
          <Text size="lg" style={{ lineHeight: 1.7 }}>
            {governingLawBody}
          </Text>
          <Text size="sm" c="ink.6">
            {legalEntityLine}
          </Text>
        </Stack>
      </MarketingSection>

      <MarketingNextSteps
        title={nextTitle}
        destinations={['/privacy', '/dispute-resolution', '/our-ethos']}
      />
    </PublicShell>
  );
}
